/* eslint-disable import/no-cycle */
import {useContext, useState} from 'react';
import {Alert, Col, Row} from 'antd';
import {observer} from 'mobx-react-lite';
import {useTranslation} from 'react-i18next';
import Form from '../../shared/form/Form';
import RemarkFormItem from './RemarkFormItem';
import Button from '../../shared/buttons/Button';
import LocationCodeChecker from './LocationCodeChecker';
import OverruleModal from './OverruleModal';
import MarkdownItem from '../../shared/MarkdownItem';
import {InspectionTaskContext} from './InspectionTaskExecutionModal';

const InspectionTaskExecutionForm = ({
  remarkRequired,
  overruledRemarkRequired,
  inspectionTask,
  form,
  initialValues = {},
  onValuesChange,
  overruleActive,
  setOverruleActive,
  setOverruleUserId,
  children,
  submitDisabled = false,
  ...props
}) => {
  const {t} = useTranslation();
  const [locationScanRequired, setLocationScanRequired] = useState(Boolean(inspectionTask.locationCode));
  const [overruleInProgress, setOverruleInProgress] = useState(false);
  const [isValueValidForOverrule, setIsValueValidForOverrule] = useState(true);
  const [isFormValid, setIsFormValid] = useState(true);
  const inspectionTaskContext = useContext(InspectionTaskContext);

  const remarkMaxLength = 255 - (inspectionTaskContext.rootForm.getFieldValue('groupRemark')?.length + 3 || 0);

  const onOverruleAllowed = (overruledUserId) => {
    setOverruleUserId(overruledUserId);
    setOverruleActive(true);
    setOverruleInProgress(false);
    form.validateFields(['value']);
  };

  const overruleClick = () => {
    form.validateFields(['remark']).then(() => {
      setOverruleInProgress(true);
    });
  };

  return (
    <>
      <Form
        form={form}
        initialValues={initialValues}
        onValuesChange={(values) => {
          if (values.locationCode) {
            setLocationScanRequired(values.locationCode !== inspectionTask.locationCode);
          }
          onValuesChange(values);
        }}
        onFieldsChange={() => {
          if (inspectionTask.quantitative) {
            const valueErrors = form.getFieldError('value');
            const decimalPlacesValidationError = t('errors.messages.maxDecimalPlaces', {
              decimalPlaces: inspectionTask.quantitative.decimalPlaces,
            });
            // ignore decimalPlacesValidationError for overrule
            setIsValueValidForOverrule(
              valueErrors.length === 0
              || (valueErrors.length === 1 && valueErrors[0] === decimalPlacesValidationError)
            );
          }
          setIsFormValid(!form.getFieldsError().some((field) => field.errors.length > 0));
        }}
        {...props}
      >
        <MarkdownItem>
          {inspectionTask.details}
        </MarkdownItem>
        <div>
          <Row gutter={[16, 16]}>
            {locationScanRequired ? (
              <Col span={24}>
                <LocationCodeChecker
                  locationCode={inspectionTask.locationCode}
                  onMatch={() => setLocationScanRequired(false)}
                />
              </Col>
            ) : (
              <>
                {children}
                <Col span={24}>
                  <RemarkFormItem
                    remarkRequired={remarkRequired}
                    form={form}
                    remarkType={'remark'}
                    hidden={overruleActive}
                    maxLength={remarkMaxLength}
                  />
                  {remarkRequired && inspectionTask.detailsNotAccepted && (
                    <Alert
                      message={t('inspectionTaskPendingModal.taskBox.action')}
                      description={<MarkdownItem>{inspectionTask.detailsNotAccepted}</MarkdownItem>}
                      type={'error'}
                    />
                  )}
                </Col>
                {overruleActive && (
                  <Col span={24}>
                    <RemarkFormItem
                      remarkRequired={overruledRemarkRequired}
                      form={form}
                      remarkType={'overruledRemark'}
                      maxLength={255}
                    />
                  </Col>
                )}
                <Col flex="auto"/>
                {!overruleActive && remarkRequired && inspectionTask.isOverruleAllowed && (
                  <Col style={{textAlign: 'right'}}>
                    <Button htmlType="button" onClick={overruleClick} disabled={!isValueValidForOverrule}>
                      {t('inspectionTaskPendingModal.taskBox.overrule')}
                    </Button>
                  </Col>
                )}
                <Col style={{textAlign: 'right'}}>
                  <Button htmlType="submit" disabled={submitDisabled || !isFormValid} onClick={form.submit}>
                    {t('inspectionTaskPendingModal.taskBox.acknowledge')}
                  </Button>
                </Col>
              </>
            )}
          </Row>
        </div>
      </Form>
      {overruleInProgress && (
        <OverruleModal
          visible={overruleInProgress}
          onCancel={() => setOverruleInProgress(false)}
          onOverruleAllowed={(overruledUserId) => onOverruleAllowed(overruledUserId)}
          inspectionTask={inspectionTask}
        />
      )}
    </>
  );
};

export default observer(InspectionTaskExecutionForm);
